import React from 'react'
import clsx from 'clsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons/faUpRightFromSquare'
import { PersonLiteDto } from '../api/person/models/PersonDto'
import { getProfilePicUrl } from '../utils/personUtils'

interface Props {
    person: PersonLiteDto
    showCurrentJobDetails?: boolean
    classes?: string
    imageClasses?: string
    nameFontSize?: string
    textSize?: string
    onSelect?: (person: PersonLiteDto) => void
}

function PersonUi({
    person,
    showCurrentJobDetails = true,
    classes,
    imageClasses,
    nameFontSize,
    textSize,
    onSelect,
}: Props): React.JSX.Element {
    const allClasses = clsx(
        'flex flex-col items-start gap-1 my-0 w-[180px]', // Ensure a default width that prevents shrinking
        classes,
    )
    const computedImageClasses = clsx(
        'rounded-full object-cover',
        imageClasses || 'w-[96px] h-[96px]', // Use imageClasses if provided, otherwise apply default
    )

    const computedNameFontSize = clsx('link-text break-words', nameFontSize)

    return (
        <div className={allClasses}>
            {getProfilePicUrl(person) ? (
                <img
                    src={getProfilePicUrl(person)}
                    alt={`${person.firstName} ${person.lastName}`}
                    className={computedImageClasses}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        e.currentTarget.onerror = null // Prevent infinite loop if fallback image also fails
                        e.currentTarget.src = '/no-profile-pic.png'
                    }}
                />
            ) : (
                <img
                    src="/no-profile-pic.png"
                    alt={`${person.firstName} ${person.lastName}`}
                    className={computedImageClasses}
                />
            )}
            <div className="flex flex-col justify-start items-start w-full">
                <div>
                    {onSelect ? (
                        <span
                            onClick={() => onSelect(person)}
                            className="link-text break-words cursor-pointer"
                        >
                            {person.firstName} {person.lastName}
                        </span>
                    ) : person.linkedInUrl ? (
                        <a
                            href={person.linkedInUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={computedNameFontSize}
                        >
                            {person.firstName} {person.lastName}{' '}
                            <FontAwesomeIcon
                                icon={faUpRightFromSquare}
                                className="text-[#0f40c9] w-3"
                            />
                        </a>
                    ) : (
                        <span className={computedNameFontSize}>
                            {person.firstName} {person.lastName}
                        </span>
                    )}
                </div>
                {showCurrentJobDetails && person.currentJobDetails && (
                    <>
                        {person.currentJobDetails ? (
                            person.currentJobDetails.variousRoles ? (
                                <div
                                    className={clsx(
                                        'italic text-sm text-gray-700 break-words',
                                        textSize,
                                    )}
                                >
                                    Various positions
                                </div>
                            ) : (
                                <div
                                    className={clsx(
                                        'text-gray-900 font-bold',
                                        textSize,
                                    )}
                                >
                                    {
                                        person.currentJobDetails.workedAt
                                            ?.jobTitle
                                    }
                                </div>
                            )
                        ) : (
                            <div
                                className={clsx(
                                    'italic text-sm text-gray-700',
                                    textSize,
                                )}
                            >
                                No position
                            </div>
                        )}
                        {person.currentJobDetails ? (
                            person.currentJobDetails.variousCompanies ? (
                                <div
                                    className={clsx(
                                        'italic text-sm text-gray-700',
                                        textSize,
                                    )}
                                >
                                    Various companies
                                </div>
                            ) : (
                                <div
                                    className={clsx(
                                        'text-sm text-gray-700 break-words',
                                        textSize,
                                    )}
                                >
                                    {person.currentJobDetails.company?.name}
                                </div>
                            )
                        ) : (
                            <div
                                className={clsx(
                                    'italic text-sm text-gray-700',
                                    textSize,
                                )}
                            >
                                No company
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default PersonUi
